import httpService from "./httpService";

export interface AccountDeletionEligibility {
  targetUserId: string;
  accountKind: "PERSONAL" | "MANAGED";
  deleted: boolean;
  eligible: boolean;
  blockers: { code: string; message: string }[];
}

export interface AccountDeletionResult {
  targetUserId: string;
  accountKind: "PERSONAL" | "MANAGED";
  state: "DELETED";
  deletedAtUtc: string;
  logoutActor: boolean;
}

export interface DeletedUserSummary {
  id: string;
  email: string;
  name: string | null;
}

const requestOptions = { requestScope: "actor", cache: "no-store" } as const;
const mutationOptions = { ...requestOptions, retryOnUnauthorized: false };

function adminUserPath(userId: string): string {
  if (!userId.trim()) throw new Error("A user ID is required.");
  return `/v3/admin/users/${encodeURIComponent(userId)}`;
}

const accountDeletionService = {
  async getPersonalEligibility(signal?: AbortSignal): Promise<AccountDeletionEligibility> {
    return (await httpService.get<AccountDeletionEligibility>("/v3/accounts/me/delete-eligibility", { ...requestOptions, signal })).data;
  },
  async deletePersonalAccount(): Promise<AccountDeletionResult> {
    return (await httpService.delete<AccountDeletionResult>("/v3/accounts/me", mutationOptions)).data;
  },
  async getAdminEligibility(userId: string, signal?: AbortSignal): Promise<AccountDeletionEligibility> {
    return (await httpService.get<AccountDeletionEligibility>(`${adminUserPath(userId)}/delete-eligibility`, { ...requestOptions, signal })).data;
  },
  async deleteUser(userId: string): Promise<AccountDeletionResult> {
    return (await httpService.delete<AccountDeletionResult>(adminUserPath(userId), mutationOptions)).data;
  },
  async listDeletedUsers(signal?: AbortSignal): Promise<DeletedUserSummary[]> {
    return (await httpService.get<{ users: DeletedUserSummary[] }>("/v3/admin/users-deleted", { ...requestOptions, signal })).data.users;
  },
  async restoreUser(userId: string): Promise<{ targetUserId: string; state: "ACTIVE"; targetNextAction: "SIGN_IN" }> {
    return (await httpService.post<{ targetUserId: string; state: "ACTIVE"; targetNextAction: "SIGN_IN" }>(`${adminUserPath(userId)}/restore`, undefined, mutationOptions)).data;
  },
};

export default accountDeletionService;
